// src/utils/timeZone.js
// The time zone every time in the portal is shown in. Each person picks it
// in My profile (saved with their settings, so it follows them to any
// browser); Eastern until they do. Set once their settings load
// (usePreferences), then read by every date formatter.

export const DEFAULT_TIME_ZONE = 'America/New_York';

// Shown first in My profile; every other zone is listed after these
export const COMMON_TIME_ZONES = [
  { id: 'America/New_York',    label: 'Eastern Time (US)' },
  { id: 'America/Chicago',     label: 'Central Time (US)' },
  { id: 'America/Denver',      label: 'Mountain Time (US)' },
  { id: 'America/Phoenix',     label: 'Arizona (US)' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { id: 'America/Anchorage',   label: 'Alaska (US)' },
  { id: 'Pacific/Honolulu',    label: 'Hawaii (US)' },
  { id: 'Asia/Kolkata',        label: 'India' },
  { id: 'Europe/London',       label: 'United Kingdom' },
];

export function isTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: tz }); return true; } catch { return false; }
}

let current = DEFAULT_TIME_ZONE;
export function setTimeZone(tz) { current = isTimeZone(tz) ? tz : DEFAULT_TIME_ZONE; }
export function timeZone() { return current; }

// Short names people know for the common zones
const SHORT = {
  'America/New_York': 'ET', 'America/Chicago': 'CT', 'America/Denver': 'MT', 'America/Phoenix': 'MST',
  'America/Los_Angeles': 'PT', 'America/Anchorage': 'AKT', 'Pacific/Honolulu': 'HST', 'Asia/Kolkata': 'IST',
};

// Short name for the zone at that moment: "ET", "PT", "IST"…
export function tzLabel(date = new Date(), tz = current) {
  if (SHORT[tz]) return SHORT[tz];
  for (const style of ['shortGeneric', 'short']) {
    try {
      const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: style })
        .formatToParts(date).find(p => p.type === 'timeZoneName');
      if (part?.value && !/^GMT[+-]/.test(part.value)) return part.value;
      if (style === 'short' && part?.value) return part.value;
    } catch { /* try the next style */ }
  }
  return tz;
}

// Every zone the browser knows, for the full list in My profile
export function allTimeZones() {
  try { return Intl.supportedValuesOf('timeZone'); } catch { return COMMON_TIME_ZONES.map(z => z.id); }
}

// "Eastern Time (US)" or "Asia/Dubai"
export function timeZoneName(tz) {
  return COMMON_TIME_ZONES.find(z => z.id === tz)?.label || tz.replace(/_/g, ' ');
}

// This browser's own zone (for "Use this device's time zone")
export function deviceTimeZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE; } catch { return DEFAULT_TIME_ZONE; }
}

// A wall-clock time ("2026-11-03T09:00", as a datetime-local box gives) in a
// time zone ↔ an ISO time (3.52.5 Announcements; shared since 3.74.0)
export function tzOffsetMs(utcMs, tz) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    .formatToParts(new Date(utcMs)).filter(x => x.type !== 'literal').map(x => [x.type, Number(x.value)]));
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - utcMs;
}
export function wallToIso(wall, tz) {
  const m = String(wall || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return null;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  let t = guess - tzOffsetMs(guess, tz);
  t = guess - tzOffsetMs(t, tz);   // again, for the hour around a clock change
  return new Date(t).toISOString();
}
export function isoToWall(iso, tz) {
  const t = Date.parse(iso); if (!t) return '';
  const d = new Date(t + tzOffsetMs(t, tz));
  return d.toISOString().slice(0, 16);
}
